import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { Navigate, useLocation } from 'react-router-dom';
import { InteractionStatus } from '@azure/msal-browser';
import { useEffect, useState } from 'react';
import { ProgressSpinner } from '@/components/ui';
import { preloadCriticalData } from '@/services/preloadService';
import { UserRole } from '@/permissions/types';
import { useCurrentUserStore } from '@/store';
import { currentUserService } from '@/services/currentUserService';
import { BackgroundRefreshService } from '@/services/backgroundRefreshService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  roles?: UserRole[];
  requireAll?: boolean;
}

export const ProtectedRoute = ({ children, roles, requireAll = false }: ProtectedRouteProps) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const location = useLocation();
  const [preloadComplete, setPreloadComplete] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [hasAttemptedFreshFetch, setHasAttemptedFreshFetch] = useState(false);
  const { user, hasFreshUserData, setUser, setLoading, setError } = useCurrentUserStore();

  const shouldBlockForFreshUserData =
    isAuthenticated && !hasFreshUserData && (!hasAttemptedFreshFetch || isLoadingUser);

  // Load current user data after authentication
  useEffect(() => {
    if (isAuthenticated && !hasFreshUserData && !isLoadingUser && !hasAttemptedFreshFetch) {
      const loadUser = async () => {
        setHasAttemptedFreshFetch(true);
        setIsLoadingUser(true);
        setLoading(true);
        try {
          // Force refresh to bypass cache and get latest user data from API
          const userData = await currentUserService.getMe(true);
          setUser(userData);
        } catch (error) {
          console.error('[ProtectedRoute] Failed to load user:', error);
          setError('Failed to load user data');
        } finally {
          setIsLoadingUser(false);
          setLoading(false);
        }
      };

      loadUser();
    }
  }, [
    isAuthenticated,
    hasFreshUserData,
    isLoadingUser,
    hasAttemptedFreshFetch,
    setUser,
    setLoading,
    setError,
  ]);

  // Preload critical data in the background after authentication and user load
  useEffect(() => {
    if (isAuthenticated && user && !preloadComplete) {
      const loadData = async () => {
        // Preload critical data in background (non-blocking)
        setPreloadComplete(true); // Allow immediate access, load in background

        try {
          await preloadCriticalData(user.roleCodes);
        } catch (error) {
          console.error('[ProtectedRoute] Background preload failed:', error);
          // Continue anyway - individual pages will handle their own data loading
        }
      };

      loadData();
    }
  }, [isAuthenticated, user, preloadComplete]);

  // Background refresh data when navigating to specific routes
  useEffect(() => {
    if (isAuthenticated && user && preloadComplete) {
      const currentPath = location.pathname;

      // Check if this route needs background refresh
      if (BackgroundRefreshService.hasRefreshConfig(currentPath)) {
        // Trigger background refresh (non-blocking, uses cached data first)
        BackgroundRefreshService.refreshForRoute(currentPath, user.roleCodes).catch(() => {
          // Don't block the UI - this is a background operation
        });
      }
    }
  }, [isAuthenticated, user, preloadComplete, location.pathname]);

  // Wait for MSAL to finish processing redirects
  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-900 dark:bg-dark-950 dark:text-white">
        <ProgressSpinner size="xl" text="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  // Show loading spinner while loading user data
  if (shouldBlockForFreshUserData || isLoadingUser || !user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 text-gray-900 dark:bg-dark-950 dark:text-white">
        <ProgressSpinner size="xl" text="Loading..." />
      </div>
    );
  }

  // Check permissions after authentication and user load
  // If roles are specified, user must have at least one (or all if requireAll=true)
  if (roles && roles.length > 0) {
    // Check if user has required roles
    const hasAccess = requireAll
      ? roles.every((role) => user.roleCodes.includes(role)) // User must have ALL required roles
      : roles.some((role) => user.roleCodes.includes(role)); // User needs ANY of the required roles

    if (!hasAccess) {
      return <Navigate to="/accessdenied" replace />;
    }
  }

  return <>{children}</>;
};
